import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { icons as lucideIcons, LucideIconData, LucideIconNode } from 'lucide-angular';

const FA_STYLE_CLASSES = new Set([
  'fa',
  'fas',
  'far',
  'fal',
  'fat',
  'fab',
  'fa-solid',
  'fa-regular',
  'fa-light',
  'fa-thin',
  'fa-duotone',
  'fa-brands'
]);

const MATERIAL_ICON_CLASSES = new Set([
  'material-icons',
  'material-icons-outlined',
  'material-icons-round',
  'material-icons-sharp',
  'material-icons-two-tone',
  'material-symbols-outlined',
  'material-symbols-rounded',
  'material-symbols-sharp'
]);

const LEGACY_ICON_ALIASES: Record<string, string> = {
  'align-left': 'align-start-horizontal',
  'alert-circle': 'circle-alert',
  'alert-triangle': 'triangle-alert',
  'arrow-circle-left': 'circle-arrow-left',
  aws: 'cloud',
  bank: 'landmark',
  'bell-slash': 'bell-off',
  blender: 'blend',
  'box-open': 'package-open',
  broom: 'sparkles',
  bullhorn: 'megaphone',
  'building-estate': 'building-2',
  'calendar-alt': 'calendar',
  'calendar-day': 'calendar-days',
  'calendar-times': 'calendar-x',
  'chart-simple': 'chart-column',
  'check-double': 'check-check',
  city: 'building-2',
  comments: 'messages-square',
  date: 'calendar-days',
  desktop: 'monitor',
  edit: 'square-pen',
  'ellipsis-h': 'ellipsis',
  'ellipsis-v': 'ellipsis-vertical',
  envelope: 'mail',
  'envelope-open': 'mail-open',
  'exchange-alt': 'arrow-left-right',
  exclamation: 'triangle-alert',
  'exclamation-circle': 'circle-alert',
  'exclamation-triangle': 'triangle-alert',
  'eye-slash': 'eye-off',
  'file-alt': 'file-text',
  'file-contract': 'file-text',
  'file-description': 'file-text',
  'file-excel': 'file-spreadsheet',
  'file-export': 'file-up',
  'file-signature': 'signature',
  'file-invoice': 'receipt',
  'file-invoice-dollar': 'receipt',
  'file-lines': 'file-text',
  'file-pdf': 'file-text',
  'file-word': 'file-text',
  filter: 'list-filter',
  cogs: 'cog',
  gear: 'cog',
  gears: 'cog',
  google: 'globe',
  'hand-holding-usd': 'hand-coins',
  'hand-paper': 'hand',
  home: 'house',
  'hourglass-half': 'hourglass',
  indent: 'list-indent-increase',
  'info-circle': 'info',
  'layer-group': 'layers',
  'layers-subtract': 'layers',
  'list-alt': 'list',
  'list-ol': 'list-ordered',
  'list-ul': 'list',
  'location-arrow': 'navigation',
  magic: 'wand-sparkles',
  'map-marker-alt': 'map-pin',
  'mobile-alt': 'smartphone',
  'money-bill': 'banknote',
  'money-bill-wave': 'banknote',
  'money-check-alt': 'receipt',
  'paint-brush': 'paintbrush',
  'paper-plane': 'send',
  'pen-to-square': 'square-pen',
  print: 'printer',
  'pen-nib': 'pen-tool',
  'photo-video': 'images',
  'quote-left': 'quote',
  refresh: 'refresh-cw',
  'ruler-combined': 'ruler',
  'rupee-sign': 'indian-rupee',
  'shield-alt': 'shield-check',
  'sign-out-alt': 'log-out',
  'sliders-h': 'sliders-horizontal',
  sms: 'message-square',
  sort: 'arrow-up-down',
  spinner: 'loader-circle',
  swatchbook: 'swatch-book',
  sync: 'refresh-cw',
  'sync-alt': 'refresh-cw',
  'tachometer-alt': 'gauge',
  tasks: 'list-checks',
  'th-large': 'grid-2x2',
  times: 'x',
  'times-circle': 'circle-x',
  'toggle-on': 'toggle-right',
  tools: 'wrench',
  'trash-alt': 'trash-2',
  'user-friends': 'users',
  'user-clock': 'clock',
  'user-shield': 'shield-user',
  'user-slash': 'user-x',
  'user-tag': 'user',
  'user-tie': 'user-round',
  'user-times': 'user-x',
  'users-cog': 'users',
  'users-slash': 'users',
  warning: 'triangle-alert',
  xmark: 'x',

  adjustments: 'sliders-horizontal',
  'adjustments-horizontal': 'sliders-horizontal',
  alert: 'triangle-alert',
  'arrow-back-up': 'undo-2',
  'building-bank': 'landmark',
  'building-plus': 'building',
  category: 'layout-grid',
  'clock-off': 'clock',
  'currency-dollar': 'dollar-sign',
  coin: 'coins',
  dashboard: 'layout-dashboard',
  door: 'door-open',
  'home-move': 'house',
  logout: 'log-out',
  'menu-2': 'menu',
  message: 'message-square',
  point: 'dot',
  speakerphone: 'megaphone',
  'bar-chart': 'chart-column',
  'credit-card': 'credit-card',
  'date-icon': 'calendar-days',
  'event-available': 'calendar-check',
  'notifications-active': 'bell-ring',
  template: 'layout-template',
  chat: 'message-square',
  visibility: 'eye',
  'warning-circle': 'circle-alert',
  'switch-2': 'arrow-left-right',
  tool: 'wrench',
  world: 'globe',
  'writing-sign': 'signature'
};

@Injectable({ providedIn: 'root' })
export class LucideLegacyIconService implements OnDestroy {
  private observer: MutationObserver | null = null;
  private readonly queue = new Set<HTMLElement>();
  private flushScheduled = false;
  private started = false;
  private readonly allIcons = lucideIcons as Record<string, LucideIconData>;
  private readonly fallbackIcon: LucideIconData = [
    ['circle', { cx: 12, cy: 12, r: 10 }]
  ];

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone
  ) {}

  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.started = true;
    this.ngZone.runOutsideAngular(() => {
      this.enqueueExistingIcons();
      this.flushQueue();
      this.startObserver();
    });
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.flushScheduled = false;
    this.queue.clear();
    this.started = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private startObserver(): void {
    const root = this.document.body;
    if (!root) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const target = mutation.target;
          if (target instanceof HTMLElement) {
            this.enqueueNode(target);
          }
          continue;
        }

        mutation.addedNodes.forEach((node) => this.enqueueNode(node));
      }
      this.scheduleFlush();
    });

    this.observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private enqueueExistingIcons(): void {
    this.document
      .querySelectorAll<HTMLElement>('i[class], span.material-icons, i.material-icons, span.material-symbols-outlined, span.material-symbols-rounded, span.material-symbols-sharp')
      .forEach((element) => this.queue.add(element));
  }

  private enqueueNode(node: Node): void {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    if (this.isPotentialLegacyIconHost(node)) {
      this.queue.add(node);
    }

    node
      .querySelectorAll<HTMLElement>('i[class], span.material-icons, i.material-icons, span.material-symbols-outlined, span.material-symbols-rounded, span.material-symbols-sharp')
      .forEach((icon) => this.queue.add(icon));
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) {
      return;
    }

    this.flushScheduled = true;
    requestAnimationFrame(() => {
      this.flushScheduled = false;
      this.flushQueue();
    });
  }

  private flushQueue(): void {
    const pending = Array.from(this.queue);
    this.queue.clear();
    pending.forEach((element) => this.renderLegacyIcon(element));
  }

  private renderLegacyIcon(element: HTMLElement): void {
    if (!element.isConnected || !element.className || !this.isPotentialLegacyIconHost(element)) {
      return;
    }

    const classes = Array.from(element.classList);
    const token = this.extractLegacyToken(element, classes);
    if (!token) {
      this.clearRenderedIcon(element);
      return;
    }

    const iconName = this.resolveIconName(token);
    const sizeScale = this.resolveSizeScale(classes);
    const spinning = this.isSpinning(classes);
    const signature = `${iconName}|${sizeScale}|${spinning ? 'spin' : 'static'}`;

    if (element.dataset['lucideLegacySignature'] === signature) {
      return;
    }

    this.clearRenderedIcon(element);

    const iconNode = this.getIconNode(iconName);
    const svg = this.createSvg(iconNode, spinning);

    if (sizeScale !== 1) {
      svg.style.width = `${sizeScale}em`;
      svg.style.height = `${sizeScale}em`;
    } else {
      svg.style.removeProperty('width');
      svg.style.removeProperty('height');
    }

    element.prepend(svg);
    element.setAttribute('data-lucide-legacy-host', 'true');
    element.dataset['lucideLegacySignature'] = signature;
  }

  private clearRenderedIcon(element: HTMLElement): void {
    element.querySelectorAll(':scope > svg.lucide-legacy-svg').forEach((svg) => svg.remove());
    element.removeAttribute('data-lucide-legacy-host');
    delete element.dataset['lucideLegacySignature'];
  }

  private extractLegacyToken(element: HTMLElement, classes: string[]): string | null {
    const faIconClass = classes.find((token) => token.startsWith('fa-') && !this.isFontAwesomeUtilityClass(token));
    if (faIconClass) {
      return faIconClass.substring(3).toLowerCase();
    }

    const tiIconClass = classes.find((token) => token.startsWith('ti-'));
    if (tiIconClass) {
      return tiIconClass.substring(3).toLowerCase();
    }

    const phIconClass = classes.find((token) => token.startsWith('ph-') && !this.isPhosphorUtilityClass(token));
    if (phIconClass) {
      return phIconClass.substring(3).toLowerCase();
    }

    if (this.isMaterialIconHost(classes)) {
      const materialToken = element.textContent?.trim().toLowerCase().replace(/[_\s]+/g, '-');
      if (materialToken) {
        return materialToken;
      }
    }

    return null;
  }

  private isPotentialLegacyIconHost(element: HTMLElement): boolean {
    return element.tagName === 'I' || this.isMaterialIconHost(Array.from(element.classList));
  }

  private isMaterialIconHost(classes: string[]): boolean {
    return classes.some((token) => MATERIAL_ICON_CLASSES.has(token));
  }

  private isFontAwesomeUtilityClass(token: string): boolean {
    if (FA_STYLE_CLASSES.has(token)) {
      return true;
    }

    if (/^fa-(xs|sm|lg|[1-9]x|10x)$/.test(token)) {
      return true;
    }

    return /^fa-(spin|spin-pulse|pulse|beat|fade|bounce|shake|flip-(horizontal|vertical|both)|rotate-(90|180|270)|fw|ul|li|stack(?:-[12]x)?|inverse|border)$/.test(
      token
    );
  }

  private isPhosphorUtilityClass(token: string): boolean {
    if (/^ph-(thin|light|regular|bold|fill|duotone)$/.test(token)) {
      return true;
    }

    if (/^ph-(xs|sm|lg|[1-9]x|10x|spin)$/.test(token)) {
      return true;
    }

    return false;
  }

  private resolveSizeScale(classes: string[]): number {
    if (classes.includes('fa-xs')) {
      return 0.75;
    }
    if (classes.includes('fa-sm')) {
      return 0.875;
    }
    if (classes.includes('fa-lg')) {
      return 1.333;
    }
    if (classes.includes('ph-sm')) {
      return 0.875;
    }
    if (classes.includes('ph-lg')) {
      return 1.333;
    }

    const scaledClass = classes.find((token) => /^fa-([1-9]|10)x$/.test(token));
    if (scaledClass) {
      const rawScale = scaledClass.replace('fa-', '').replace('x', '');
      const parsed = Number.parseFloat(rawScale);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    const phScaledClass = classes.find((token) => /^ph-([1-9]|10)x$/.test(token));
    if (!phScaledClass) {
      return 1;
    }

    const rawScale = phScaledClass.replace('ph-', '').replace('x', '');
    const parsed = Number.parseFloat(rawScale);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  private isSpinning(classes: string[]): boolean {
    return (
      classes.includes('fa-spin') ||
      classes.includes('fa-pulse') ||
      classes.includes('fa-spin-pulse') ||
      classes.includes('ti-spin') ||
      classes.includes('ph-spin')
    );
  }

  private resolveIconName(token: string): string {
    const normalized = token.toLowerCase().trim();
    const mapped = LEGACY_ICON_ALIASES[normalized];
    if (mapped && this.hasIcon(mapped)) {
      return mapped;
    }

    if (this.hasIcon(normalized)) {
      return normalized;
    }

    if (normalized.endsWith('-alt')) {
      const withoutAlt = normalized.replace(/-alt$/, '');
      if (this.hasIcon(withoutAlt)) {
        return withoutAlt;
      }
    }

    if (normalized.endsWith('-circle')) {
      const circleVariant = `circle-${normalized.replace(/-circle$/, '')}`;
      if (this.hasIcon(circleVariant)) {
        return circleVariant;
      }
    }

    return 'circle';
  }

  private hasIcon(kebabName: string): boolean {
    return this.isIconData(this.allIcons[this.toPascalCase(kebabName)]);
  }

  private getIconNode(kebabName: string): LucideIconData {
    const resolved = this.allIcons[this.toPascalCase(kebabName)] ?? this.allIcons['Circle'];
    if (this.isIconData(resolved)) {
      return resolved;
    }
    return this.fallbackIcon;
  }

  private toPascalCase(value: string): string {
    return value
      .split(/[-_\s]+/)
      .filter((segment) => segment.length > 0)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join('');
  }

  private isIconData(iconData: unknown): iconData is LucideIconData {
    return Array.isArray(iconData);
  }

  private createSvg(iconNode: LucideIconData, spinning: boolean): SVGSVGElement {
    const svg = this.document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const classes = spinning ? 'lucide-legacy-svg lucide-legacy-spin' : 'lucide-legacy-svg';

    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', classes);

    for (const node of iconNode) {
      const [tag, attrs] = node as LucideIconNode;
      const child = this.document.createElementNS('http://www.w3.org/2000/svg', tag);
      Object.entries(attrs).forEach(([attrName, attrValue]) => {
        if (attrValue === null || attrValue === undefined) {
          return;
        }
        child.setAttribute(attrName, String(attrValue));
      });
      svg.appendChild(child);
    }

    return svg;
  }
}
